/**
 * Parsed `/content?mode=&post=` params — the route contract of the Content Performance screen
 * (SPEC `Content Performance` § Navigation & chrome).
 *
 * `useViewRoute`'s shape, narrowed to the three content modes: the screen owns `?mode=`, while
 * `?post=` stays exactly what `PostPreviewProvider` already reads on every other route (an enriched
 * Posts row opens the shared preview popup). Lives in the shell so the feature folder never touches
 * URL handling.
 */

import { useCallback } from 'react';
import { useSearchParams } from 'react-router';
import type { ContentMode } from '@lw/shared';
import { DEFAULT_CONTENT_MODE, VIEW_MODE_PARAM, parseContentMode } from './routes';

export type ContentRoute = {
  mode: ContentMode;
  setMode: (mode: ContentMode) => void;
  /** `true` when the URL names a mode explicitly — the §12.4 override of the persisted one. */
  hasExplicitMode: boolean;
};

export function useContentRoute(): ContentRoute {
  const [searchParams, setSearchParams] = useSearchParams();
  const mode = parseContentMode(searchParams.get(VIEW_MODE_PARAM));
  const hasExplicitMode = searchParams.has(VIEW_MODE_PARAM);

  const setMode = useCallback(
    (next: ContentMode) => {
      setSearchParams(
        (previous) => {
          const updated = new URLSearchParams(previous);
          // The default mode is the bare URL, so a shared link never carries a redundant param.
          if (next === DEFAULT_CONTENT_MODE) updated.delete(VIEW_MODE_PARAM);
          else updated.set(VIEW_MODE_PARAM, next);
          return updated;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return { mode, setMode, hasExplicitMode };
}
